import React, { useMemo } from 'react';
import styles from './BackgroundTable.module.css';
import PhenomTableLogo from '../PhenomTableLogo/PhenomTableLogo';
import useIsMobileView from '@/hooks/useIsMobileView';
import { twMerge } from 'tailwind-merge';

export enum Variant {
  WinPot = 'win-pot',
  Default = 'default',
  Custom = 'custom',
}

interface BackgroundTableProps {
  variant?: Variant;
  customImageSrc?: string;
  feltColor: string;
  showLogo: boolean;
}

const BackgroundTable: React.FC<BackgroundTableProps> = ({
  variant = Variant.Default,
  customImageSrc = '',
  feltColor = 'purple',
}) => {
  const isMobileView = useIsMobileView({ checkForUserAgent: true });

  const useBackgroundDefault = () =>
    useMemo(() => {
      switch (feltColor) {
        case 'green':
          return styles.backgroundDefaultGreen;
        case 'grey':
          return styles.backgroundDefaultGrey;
        case 'red':
          return styles.backgroundDefaultRed;
        case 'blue':
          return styles.backgroundDefaultBlue;
        case 'white':
          return styles.backgroundDefaultWhite;
        case 'black':
          return styles.backgroundDefaultBlack;
        default:
          return styles.backgroundDefault;
      }
    }, [feltColor]);

  const useBackgroundDefaultHighlight = () =>
    useMemo(() => {
      switch (feltColor) {
        case 'green':
          return styles.backgroundDefaultHighlightGreen;
        case 'grey':
          return styles.backgroundDefaultHighlightGreen;
        case 'red':
          return styles.backgroundDefaultHighlightRed;
        case 'blue':
          return styles.backgroundDefaultHighlightBlue;
        case 'white':
          return styles.backgroundDefaultHighlightWhite;
        case 'black':
          return styles.backgroundDefaultHighlightBlack;
        default:
          return styles.backgroundDefaultHighlight;
      }
    }, [feltColor]);

  const useBorderStyle = () =>
    useMemo(() => {
      return `border-ph-${feltColor}`;
    }, [feltColor]);

  const useBackgroundGlow = () =>
    useMemo(() => {
      return `bg-ph-${feltColor} ${styles.customBackground} ${styles.glowBase}`;
    }, [feltColor]);

  const backgroundStyle = useBackgroundDefault();

  const highlightStyle = useBackgroundDefaultHighlight();

  const borderStyle = useBorderStyle();

  const backgroundGlow = useBackgroundGlow();

  const variantCustom = variant === Variant.Custom;

  return (
    <div className={twMerge(styles.backgroundBase, backgroundStyle, 'flex items-center justify-center')}>
      <div className={twMerge('relative flex h-full w-full items-center justify-center', highlightStyle)}>
        {!variantCustom && <div className={twMerge(styles.border, borderStyle)} />}
        {variantCustom && <img className={styles.customBackground} src={customImageSrc} alt="custom" />}
        <div className={backgroundGlow} />
        {!isMobileView && (
          <PhenomTableLogo className="!absolute left-1/2 top-1/2 hidden w-1/2 -translate-x-1/2 -translate-y-1/2 md:block" />
        )}
      </div>
    </div>
  );
};

export default BackgroundTable;
